/**
 * Custom Form Component
 * Handles validation for all form inputs across the theme
 */

class CustomForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.originalButtonText = this.submitButton?.innerHTML || '';
    
    // Input elements that need validation
    this.emailInputs = this.querySelectorAll('input[type="email"]');
    this.keyCodeInputs = this.querySelectorAll('[data-key-code-input]');
    this.keyNumberInputs = this.querySelectorAll('[data-key-number-input]');
    this.requiredInputs = this.querySelectorAll('[required]');
    this.validationInputs = this.querySelectorAll('[data-form-input-validation]');
    
    this.init();
  }
  
  init() {
    if (!this.form) return;
    
    // Set up event listeners for all input types
    this.setupEmailValidation();
    this.setupKeyCodeValidation();
    this.setupKeyNumberValidation();
    
    // Add input event listeners to all required fields for form validation
    this.requiredInputs.forEach(input => {
      input.addEventListener('input', () => this.validateForm());
    });
    
    // Initial validation
    this.validateForm();
    
    // Log initialization
    console.log('CustomForm initialized:', this.form);
  }
  
  setupEmailValidation() {
    this.emailInputs.forEach(input => {
      const errorId = `Email-${input.id}-error`;
      
      // Create error element if it doesn't exist
      let errorElement = document.getElementById(errorId);
      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.id = errorId;
        errorElement.className = 'field__error';
        errorElement.hidden = true;
        errorElement.textContent = 'Please enter a valid email address';
        input.parentNode.appendChild(errorElement);
        
        // Add aria-describedby attribute
        input.setAttribute('aria-describedby', errorId);
        input.setAttribute('data-form-input-validation', '');
      }
      
      input.addEventListener('input', () => this.validateEmail(input, errorElement));
      
      // Initial validation
      this.validateEmail(input, errorElement);
    });
  }
  
  validateEmail(input, errorElement) {
    const value = input.value.trim();
    const isEmpty = value.length === 0;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = isEmpty || emailRegex.test(value);
    
    if (!isValid) {
      errorElement.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('error');
    } else {
      errorElement.hidden = true;
      input.removeAttribute('aria-invalid');
      input.classList.remove('error');
    }
    
    this.validateForm();
    return isValid;
  }
  
  setupKeyCodeValidation() {
    this.keyCodeInputs.forEach(input => {
      const errorId = input.getAttribute('aria-describedby') || `KeyCode-${input.id}-error`;
      const errorElement = document.getElementById(errorId);
      
      if (!errorElement) return;
      
      input.addEventListener('input', () => this.validateKeyCode(input, errorElement));
      
      // Initial validation
      this.validateKeyCode(input, errorElement);
    });
  }
  
  validateKeyCode(input, errorElement) {
    // Only allow digits
    input.value = input.value.replace(/[^0-9]/g, '');
    
    // Check validation (exactly 7 digits)
    const isValid = input.value.length === 7;
    const isEmpty = input.value.length === 0;
    
    if (!isEmpty && !isValid) {
      errorElement.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('error');
    } else {
      errorElement.hidden = true;
      input.removeAttribute('aria-invalid');
      input.classList.remove('error');
    }
    
    this.validateForm();
    return isValid || isEmpty;
  }
  
  setupKeyNumberValidation() {
    this.keyNumberInputs.forEach(input => {
      const errorId = input.getAttribute('aria-describedby') || `KeyNumber-${input.id}-error`;
      const errorElement = document.getElementById(errorId);
      
      if (!errorElement) return;
      
      input.addEventListener('input', () => this.validateKeyNumber(input, errorElement));
      
      // Initial validation
      this.validateKeyNumber(input, errorElement);
    });
  }
  
  validateKeyNumber(input, errorElement) {
    // Allow only alphanumeric characters
    input.value = input.value.replace(/[^a-zA-Z0-9]/g, '');
    
    // Check validation (3-7 alphanumeric characters)
    const isValid = input.value.length >= 3 && input.value.length <= 7;
    const isEmpty = input.value.length === 0;
    
    if (!isEmpty && !isValid) {
      errorElement.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('error');
    } else {
      errorElement.hidden = true;
      input.removeAttribute('aria-invalid');
      input.classList.remove('error');
    }
    
    this.validateForm();
    return isValid || isEmpty;
  }
  
  validateForm() {
    if (!this.form || !this.submitButton) return;
    
    let formValid = true;
    
    // Check required fields
    this.requiredInputs.forEach(input => {
      if (input.hasAttribute('required') && !input.value.trim()) {
        formValid = false;
      }
    });
    
    // Check validation fields
    this.validationInputs.forEach(input => {
      if (input.getAttribute('aria-invalid') === 'true') {
        formValid = false;
      }
    });
    
    // Update submit button state
    this.submitButton.disabled = !formValid;
    this.submitButton.classList.toggle('button--disabled', !formValid);
    
    console.log('Form validation state:', formValid);
    return formValid;
  }
}

// Register the custom element
if (typeof customElements !== 'undefined') {
  customElements.define('custom-form', CustomForm);
}

// Support for legacy browsers
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing custom form components');
}); 